import { InternalLink } from "@/components/InternalLink";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button as DsButton } from "@/design-system/Button/Button";
import {
  type DropdownItem,
  MultiDropdown,
} from "@/design-system/Dropdown/Dropdown";
import { SortDropdown } from "@/design-system/Dropdown/SortDropdown";
import { CategoryTab } from "@/design-system/Tabs/CategoryTab";
import { CoinIcon } from "@/designSystemIcons";
import { trackViewContent } from "@/events/evolutionsMixpanel";
import { expire as expireIcon, filter as filterIcon } from "@/icons";
import { QuickFilter } from "@/pages/Players/QuickFilters";
import {
  Evolution,
  evolutionCategoriesByIdSelector,
  evolutionCategoriesOrderedSelector,
  overviewSortSelector,
  setOverviewSort,
} from "@/redux/evolutions/evolutionsSlice";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { BREAKPOINTS } from "@/settings";
import {
  type EvolutionsOverviewSort,
  OVERVIEW_STATE_TABS,
  type OverviewOtherFilter,
  type OverviewStateTab,
  evolutionsOverviewSortItems,
  filterOverviewEvolutions,
  overviewStateCounts,
  sortEvolutionsForOverview,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import {
  EVOLUTION_STATES_GUIDE_PATH,
  evolutionOverviewPath,
  toggleInList,
} from "@repo/shared/utils";
import { Sparkles } from "lucide-react";
import { Fragment, ReactNode, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useSearchParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
  EvolutionOverviewCard,
  EvolutionOverviewCardSkeleton,
} from "./EvolutionOverviewCard";
import { SORT_LABEL_KEYS, evoStateItems } from "./EvolutionsOverviewPage";

const GRID_CLASS =
  "grid grid-cols-1 gap-4 mobile:grid-cols-[repeat(auto-fill,minmax(19rem,1fr))]";

/** One-tap filters beside the Filters button; the rest live in its panel. */
const QUICK_FILTERS = ["new", "free", "expiring"] as const;

// The URL keeps the state tab, under the slugs the old sidebar used.
const tabToParam = (tab: OverviewStateTab) =>
  tab === "all"
    ? "all"
    : (evoStateItems.find((i) => i.state === tab)?.key ?? tab);
const paramToTab = (param: string | null): OverviewStateTab =>
  evoStateItems.find((i) => i.key === param)?.state ?? "all";

interface EvolutionsOverviewContentProps {
  evolutions: Evolution[];
  isLoading?: boolean;
  selectMode: boolean;
  selectedEvolutions: Evolution[];
  onSelectEvo: (evo: Evolution) => void;
  searchTerm: string;
  handleSelectMode: () => void;
  handleChangeStatusClick: () => void;
}

export const EvolutionsOverviewContent = ({
  evolutions,
  isLoading = false,
  selectMode,
  selectedEvolutions = [],
  onSelectEvo,
  searchTerm,
  handleSelectMode,
  handleChangeStatusClick,
}: EvolutionsOverviewContentProps) => {
  const { t } = useTranslation(["evolutions", "common"]);
  const dispatch = useDispatch();
  const categoriesById = useSelector(evolutionCategoriesByIdSelector);
  const orderedCategories = useSelector(evolutionCategoriesOrderedSelector);
  const stickyTop = useSelector(topToPageSelector).value;
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = paramToTab(searchParams.get("filter"));
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [other, setOther] = useState<OverviewOtherFilter[]>([]);

  const setTab = (next: OverviewStateTab) => {
    // Start the new list at the top. Before setSearchParams: the router
    // snapshots scrollY when the navigation starts and restores it after.
    window.scrollTo(0, 0);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("filter", tabToParam(next));
        return params;
      },
      { replace: true, preventScrollReset: true },
    );
  };

  const counts = useMemo(() => overviewStateCounts(evolutions), [evolutions]);
  const overviewSort = useSelector(overviewSortSelector);
  const sortedEvolutions = useMemo(
    () =>
      sortEvolutionsForOverview(
        filterOverviewEvolutions(
          evolutions,
          {
            // A search spans every tab.
            states: searchTerm || tab === "all" ? [] : [tab],
            categoryIds,
            other,
            searchTerm,
          },
          Date.now(),
        ),
        overviewSort,
      ),
    [evolutions, tab, categoryIds, other, searchTerm, overviewSort],
  );

  const tabLabel = (state: OverviewStateTab) =>
    state === "all"
      ? t("picker.all")
      : t(evoStateItems.find((i) => i.state === state)!.labelKey);
  const activeStateItem = evoStateItems.find((i) => i.state === tab);

  const sortItems = evolutionsOverviewSortItems.map((item) => ({
    value: item.value,
    label: t(SORT_LABEL_KEYS[item.value]),
  }));

  // The panel holds what the quick row doesn't: categories.
  const panelItems = useMemo((): DropdownItem[] => {
    const used = new Set(evolutions.map((evo) => evo.categoryId));
    return [
      { value: "h-category", label: t("info.category"), header: true },
      ...orderedCategories
        .filter((c) => used.has(c.id))
        .map((c) => ({ value: `c-${c.id}`, label: c.name })),
    ];
  }, [evolutions, orderedCategories, t]);
  const panelSelected = categoryIds.map((id) => `c-${id}`);
  const onPanelChange = (item: DropdownItem) =>
    setCategoryIds((ids) => toggleInList(ids, Number(item.value.slice(2))));

  const quickFilter: Record<
    (typeof QUICK_FILTERS)[number],
    { label: string; icon: ReactNode }
  > = {
    new: {
      label: t("overview.sort.new"),
      icon: <Sparkles className="size-4" />,
    },
    free: { label: t("common:free"), icon: <CoinIcon size={14} /> },
    expiring: {
      label: t("picker.expiringSoon"),
      icon: <span className="size-3.5 fill-current">{expireIcon}</span>,
    },
  };

  // What the empty state names, so it says what came up empty.
  const activeFilterNames = [
    searchTerm ? `"${searchTerm}"` : tabLabel(tab),
    ...other.map((key) =>
      key === "paid" ? t("picker.paid") : quickFilter[key].label,
    ),
    ...categoryIds.map((id) => categoriesById[id]?.name ?? ""),
  ].filter(Boolean);

  return (
    <div className="mx-auto flex w-full max-w-content flex-col px-4 large:px-16 touch-device:px-4">
      {/* State tabs, as the SBC page's categories. A search spans them all. */}
      <ScrollArea enableShadows className="-mx-4 pt-3">
        <div className="flex w-max items-center gap-x-1 px-4 touch-device:-ml-2">
          {OVERVIEW_STATE_TABS.map((state) => (
            <Fragment key={state}>
              <CategoryTab
                label={`${tabLabel(state)} (${counts[state]})`}
                isActive={!searchTerm && tab === state}
                onClick={() => setTab(state)}
              />
              {/* "All" stands apart from the states it spans. */}
              {state === "all" && (
                <span className="mx-1 size-1 shrink-0 rounded-full bg-foreground-disabled" />
              )}
            </Fragment>
          ))}
        </div>
        <ScrollBar orientation="horizontal" className="h-1.5" />
      </ScrollArea>
      <div className="flex min-h-5 flex-wrap items-center justify-between gap-x-4 gap-y-1 pt-2">
        <p className="text-sm text-foreground-muted">
          {!searchTerm && (
            <span>
              {activeStateItem
                ? t(activeStateItem.descriptionKey)
                : t("overview.allDescription")}
            </span>
          )}
        </p>
      </div>

      {/* Filters and quick filters left, sort right: the /players row. */}
      <div className="flex items-center gap-x-3 pt-3 max-mobile:flex-wrap max-mobile:gap-y-2">
        {/* The panel opens outside the scroll area, which would clip it. */}
        <div className="flex min-w-0 flex-1 items-center gap-x-2 max-mobile:basis-full">
          <MultiDropdown
            items={panelItems}
            selected={panelSelected}
            onChange={onPanelChange}
            label={t("common:filters")}
            hideSearch
            width={240}
            trigger={
              <DsButton
                variant="secondary"
                size="small"
                icon={<div className="size-4">{filterIcon}</div>}
              >
                <span className="flex items-center gap-x-1">
                  <span>{t("common:filters")}</span>
                  {panelSelected.length > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-control text-[10px] font-bold text-foreground-muted">
                      {panelSelected.length}
                    </span>
                  )}
                </span>
              </DsButton>
            }
          />
          <span className="size-1 shrink-0 rounded-full bg-foreground-disabled" />
          <ScrollArea enableShadows className="min-w-0 flex-1 max-mobile:-mr-4">
            <div className="flex w-max items-center gap-x-2 max-mobile:pr-4">
              {QUICK_FILTERS.map((key) => (
                <QuickFilter
                  key={key}
                  label={quickFilter[key].label}
                  icon={quickFilter[key].icon}
                  selected={other.includes(key)}
                  onClick={() =>
                    setOther((o) => toggleInList<OverviewOtherFilter>(o, key))
                  }
                />
              ))}
            </div>
            <ScrollBar orientation="horizontal" className="h-1.5" />
          </ScrollArea>
        </div>
        {/* On phones the guide link shares the sort's row; desktop has it in the band. */}
        <div className="ml-auto flex min-w-0 items-center gap-x-4 max-mobile:basis-full max-mobile:justify-between">
          <span className="flex min-w-0 text-sm mobile:hidden">
            <InternalLink to={EVOLUTION_STATES_GUIDE_PATH} truncate>
              {t("overview.statesHelp")}
            </InternalLink>
          </span>
          <div className="shrink-0">
            <SortDropdown
              items={sortItems}
              selected={overviewSort}
              onChange={(value) =>
                dispatch(setOverviewSort(value as EvolutionsOverviewSort))
              }
            />
          </div>
        </div>
      </div>

      {/* Entering select mode is the band's "Manage States" sheet on phones;
          this row only carries the in-mode controls, stuck under the navbar. */}
      {isMobile && selectMode && (
        <div
          className="sticky z-20 -mx-4 mt-3 flex flex-row gap-x-2 bg-background px-4 py-3"
          style={{ top: `${stickyTop}px` }}
        >
          <DsButton variant="secondary" size="small" onClick={handleSelectMode}>
            {t("common:cancel")}
          </DsButton>
          <DsButton
            variant="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleChangeStatusClick();
            }}
          >
            {t("overview.confirmSelection", {
              count: selectedEvolutions.length,
            })}
          </DsButton>
        </div>
      )}

      <div className="py-4">
        {/*
          Ahead of the empty branch: before the game ships nothing fetches the
          evolutions, so `isLoading` never clears.
        */}
        {isLoading ? (
          <div className={GRID_CLASS}>
            {Array.from({ length: 8 }).map((_, i) => (
              <EvolutionOverviewCardSkeleton key={i} />
            ))}
          </div>
        ) : sortedEvolutions.length === 0 ? (
          <div className="flex flex-col items-center gap-y-3 py-12 text-center">
            <p className="text-foreground-muted">
              {t("overview.noEvolutionsMatch", {
                filters: activeFilterNames.join(" · "),
              })}
            </p>
            {(categoryIds.length > 0 || other.length > 0) && (
              <DsButton
                variant="secondary"
                size="small"
                onClick={() => {
                  setCategoryIds([]);
                  setOther([]);
                }}
              >
                {t("common:clearFilters")}
              </DsButton>
            )}
          </div>
        ) : (
          <div className={GRID_CLASS}>
            {sortedEvolutions.map((evo) =>
              selectMode ? (
                <EvolutionOverviewCard
                  key={evo.id}
                  evolution={evo}
                  category={categoriesById[evo.categoryId]}
                  isSelected={selectedEvolutions.includes(evo)}
                  onClick={() => onSelectEvo(evo)}
                  selectMode
                />
              ) : (
                <NavLink
                  key={evo.id}
                  to={evolutionOverviewPath(evo)}
                  onClick={() =>
                    trackViewContent({
                      subsection: "Overview",
                      content_type: "evolution",
                      action_type: "core",
                      new_content: !!evo.newEvolution,
                    })
                  }
                >
                  <EvolutionOverviewCard
                    evolution={evo}
                    category={categoriesById[evo.categoryId]}
                  />
                </NavLink>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
};
